// Mileage pricing for the expense form: miles times the rate in force on the
// day of the trip. The rates come from the server (GET /expenses/meta/mileage-
// rates, oldest first) and the server reprices on save, so this is the preview
// of what it will store. Keep the arithmetic in step with
// server/utils/mileageRates.js.

export const MILEAGE_CATEGORY = 'mileage';

/** Digits and at most one decimal point, with no more than two decimals. */
export const sanitizeMiles = (value) => {
  if (value == null) return '';
  const onlyAllowed = String(value).replace(/[^0-9.]/g, '');
  const firstDotIndex = onlyAllowed.indexOf('.');
  if (firstDotIndex < 0) return onlyAllowed;
  const before = onlyAllowed.slice(0, firstDotIndex + 1);
  const after = onlyAllowed.slice(firstDotIndex + 1).replace(/\./g, '').slice(0, 2);
  return `${before}${after}`;
};

// Dollars, to the cent, computed in integers so 0.725 can't round the wrong
// way as a float. Matches the server exactly.
export const computeMileageAmount = (miles, ratePerMile) => {
  const milesHundredths = Math.round(Number(miles) * 100);
  const rateTenThousandths = Math.round(Number(ratePerMile) * 10000);
  const cents = Math.round((milesHundredths * rateTenThousandths) / 10000);
  return cents / 100;
};

// The rate in force on a YYYY-MM-DD date: the latest one starting on or
// before it. `rates` is oldest first. Null when the date predates every rate.
export const findMileageRate = (rates, isoDate) => {
  if (!isoDate || !Array.isArray(rates)) return null;
  let match = null;
  rates.forEach((rate) => {
    if (rate.effective_date <= isoDate) match = rate;
  });
  return match;
};

// A rate that takes effect partway through start..end, if any. A trip like
// that can't be priced at one rate, so the server refuses it.
export const findMileageRateChange = (rates, startIso, endIso) => {
  if (!startIso || !endIso || startIso === endIso || !Array.isArray(rates)) return null;
  return rates.find((rate) => rate.effective_date > startIso && rate.effective_date <= endIso) || null;
};

// "$0.725" / "$0.76": as many decimals as the rate has, at least two, so a
// half-cent rate isn't shown rounded.
export const formatMileageRate = (ratePerMile) => {
  const value = Number(ratePerMile);
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
};

// "2026-07-01" -> "07/01/2026".
export const formatIsoDate = (isoDate) => {
  const [year, month, day] = String(isoDate || '').split('-');
  return year && month && day ? `${month}/${day}/${year}` : '';
};
